/**
 * The two hosts this app has to know about, read once from the build's environment.
 *
 * **Both are configuration, not constants.** They differ per environment, and the repo's
 * split is that Terraform owns configuration and the deploy owns content: the platform
 * stack publishes each to SSM and this app's CD injects them (`stytch.tf`,
 * `.github/workflows/apps-my-cd.yml`).
 *
 * **They fail closed, and `null` is a state the app renders rather than a crash.** A bundle
 * built without one of these cannot work — every request would go to `undefined/app/...` —
 * so `main.tsx` shows the calm config-error card instead, exactly as it does for a missing
 * Stytch public token. The alternative, a throw at module load, is a white screen.
 */

/** Trim a trailing slash so `${origin}${path}` never produces `//app`. */
function origin(value: string | undefined): string | null {
  if (value === undefined || value.length === 0) return null;
  return value.replace(/\/+$/, "");
}

/** Where `/app/*` lives — `https://mcp[-dev].iamboone.app`. */
export const MCP_ORIGIN = origin(import.meta.env.VITE_MCP_ORIGIN);

/**
 * Where signing in happens — `https://login[-dev].iamboone.app`.
 *
 * This host runs no sign-in flow (src/stytch.ts), so a visitor with no session is sent
 * there and returned by `?return_to=`, which the login app validates against
 * `*.iamboone.app` before honouring it.
 */
export const LOGIN_ORIGIN = origin(import.meta.env.VITE_LOGIN_ORIGIN);

/**
 * The sign-in URL for whatever the browser is currently looking at.
 *
 * Built from `window.location.href` so a member who followed a deep link into a family
 * comes back to it rather than to the panel's root. The login app is what decides whether
 * this URL is safe to return to — it honours only a first-party `https://*.iamboone.app` —
 * so a value from this bundle is never trusted there either.
 */
export function signInUrl(returnTo: string = window.location.href): string | null {
  if (LOGIN_ORIGIN === null) return null;
  return `${LOGIN_ORIGIN}/login?return_to=${encodeURIComponent(returnTo)}`;
}
